import notification from "antd/es/notification";
import { defineMessage } from "react-intl";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { v4 } from "uuid";

import { isNotEmptyString } from "@mapmycustomers/shared";
import { EntityType } from "@mapmycustomers/shared/enum";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import { Person } from "@mapmycustomers/shared/types/entity";
import Company from "@mapmycustomers/shared/types/entity/Company";
import {
  DuplicateEntity,
  DuplicateSearchFilter,
} from "@mapmycustomers/shared/types/entity/Duplicate";
import File, { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import {
  PlatformCombinedCondition,
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import i18nService from "@app/config/I18nService";
import { CreateCompanyPayload, FieldValidationErrors } from "@app/store/api/ApiService";
import { RequestOptions } from "@app/store/api/BaseApiService";
import { callApi } from "@app/store/api/callApi";
import { showEntityView } from "@app/store/entityView/actions";
import { showEntityChannel } from "@app/store/entityView/sagas";
import { handleError } from "@app/store/errors/actions";
import {
  getDuplicateCheckSourceObjectFilter,
  getOrganization,
  getOrganizationId,
} from "@app/store/iam";
import { selectSourceObjectMessageArgs } from "@app/store/iam/selectSourceObjectMessageArgs";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import FileListItem from "@app/types/FileListItem";
import { DUPLICATE_SEARCH_RADIUS, NOTIFICATION_DURATION_WITH_ACTION } from "@app/util/consts";
import { allSettled, SettleResult } from "@app/util/effects";
import { SourceObjectMessageArgs } from "@app/util/ui";

import getCreationGeocodeErrorMessage from "../../util/getCreationGeocodeErrorMessage";
import getSuccessNotificationNode from "../../util/getSuccessNotificationNode";

import { getFileGroupId, getUploadedFileListIds } from ".";
import {
  clearAllUploadedCompanyFiles,
  createCompany,
  initialize,
  removeCompanyFile,
  uploadCompanyFiles,
} from "./actions";

const successMessage = defineMessage({
  id: "createCompanyModal.success",
  defaultMessage:
    "{sourceObject, select, standard {Company} leadCompanies {Lead} siteCompanies {Site} projectCompanies {Project} shopCompanies {Shop} other {{label}}} added successfully",
  description:
    'Company add success message. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
});

export function* onInitialize() {
  try {
    const org: Organization = yield select(getOrganization);
    const companiesResponse: ListResponse<Company> = yield callApi("fetchCompanies", org.id, {
      $columns: ["id", "name"],
      $limit: 100,
      $order: "name",
    });
    yield put(
      initialize.success({ availableCompanies: companiesResponse.data, fileGroupId: v4() })
    );
  } catch (error) {
    yield put(initialize.failure());
    yield put(handleError({ error }));
  }
}

export function* onCreateCompany({ payload }: ReturnType<typeof createCompany.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const {
      callback,
      checkDuplicates,
      company,
      customFieldsValues,
      fieldsValueValidateCallback,
      groupsIdsToAdd,
      layoutId,
      leadGen,
      personId,
    } = payload;

    if (fieldsValueValidateCallback) {
      // Validate fields values
      let fieldErrors: FieldValidationErrors[] = [];

      const standardFieldsValidationErrors: FieldValidationErrors[] = yield callApi(
        "validateStandardFields",
        orgId,
        layoutId,
        EntityType.COMPANY,
        company
      );
      if (standardFieldsValidationErrors.length) {
        fieldErrors = standardFieldsValidationErrors;
      }

      if (customFieldsValues.length) {
        const customFieldValidateResponse: FieldValidationErrors[] = yield callApi(
          "validateCustomFieldsValues",
          orgId,
          layoutId,
          EntityType.COMPANY,
          undefined,
          customFieldsValues
        );

        if (customFieldValidateResponse.length) {
          fieldErrors = [...fieldErrors, ...customFieldValidateResponse];
        }
      }

      if (fieldErrors.length) {
        yield call(fieldsValueValidateCallback, fieldErrors);
        yield put(createCompany.failure({}));
        return;
      }
    }

    if (checkDuplicates) {
      const { email, geoPoint, name, phone, website } = company;
      const nameAddressCondition: PlatformCombinedCondition = {
        $and: [],
      };
      if (isNotEmptyString(name)) {
        nameAddressCondition.$and!.push({ name: { $eq: name.trim(), $fuzzy: true } });
      }

      const fieldsFilter: PlatformFilterCondition[] = [
        ...((phone ?? "").trim().length ? [{ phone }] : []),
        ...((email ?? "").trim().length ? [{ email }] : []),
        ...((website ?? "").trim().length ? [{ domain: website }] : []),
      ];
      if (geoPoint) {
        nameAddressCondition.$and!.push({
          area: { point: geoPoint.coordinates, radius: DUPLICATE_SEARCH_RADIUS, uom: "metres" },
        });
      } else {
        const addressToCheck = {
          address: company.address ?? "",
          city: company.city ?? "",
          country: company.country ?? "",
          postalCode: company.postalCode ?? "",
          region: company.region ?? "",
        };
        if (Object.values(addressToCheck).some((value) => value.trim().length > 0)) {
          const response: GeocodeResult = yield callApi("geocodeAddress", orgId, addressToCheck);
          if (response.geoPoint) {
            nameAddressCondition.$and!.push({
              area: {
                point: response.geoPoint.coordinates,
                radius: DUPLICATE_SEARCH_RADIUS,
                uom: "metres",
              },
            });
          }
        }
      }
      if (nameAddressCondition.$and!.length) {
        fieldsFilter.push(nameAddressCondition);
      }

      // Scoped to the org default because that is what the server stamps on a new record, whatever
      // the client sends. A record type picker on this form would need to revisit this.
      const sourceObjectFilter: Pick<DuplicateSearchFilter, "sourceObject"> = yield select(
        getDuplicateCheckSourceObjectFilter,
        EntityType.COMPANY
      );
      const response: ListResponse<DuplicateEntity> = yield callApi(
        "fetchEntityDuplicates",
        orgId,
        EntityType.COMPANY,
        { $filters: { $or: fieldsFilter, dupeCheck: true, ...sourceObjectFilter } }
      );

      if (response.data.length) {
        yield put(
          createCompany.failure({
            duplicates: response.data,
          })
        );
        return;
      }
    }

    const fileIds: File["id"][] = yield select(getUploadedFileListIds);
    const companyToSend: CreateCompanyPayload = {
      ...company,
      files: fileIds.length > 0 ? [...fileIds] : undefined,
    };
    const headers: RequestOptions["headers"] = {};
    if (leadGen) {
      headers["x-mmc-source"] = "lead-gen";
    }
    const newCompany: Company = yield callApi("createCompany", orgId, layoutId, companyToSend, {
      headers,
    });
    const companyIds: Array<Company["id"]> = [newCompany.id];

    yield all(
      groupsIdsToAdd.map((groupId) =>
        callApi("addToGroup", orgId, groupId, EntityType.COMPANY, companyIds)
      )
    );
    if (customFieldsValues.length) {
      yield callApi(
        "createCustomFieldsValues",
        orgId,
        layoutId,
        EntityType.COMPANY,
        newCompany.id,
        customFieldsValues
      );
    }
    if (personId) {
      const person: Person | undefined = yield callApi("fetchPerson", orgId, personId);
      if (person) {
        yield callApi("updatePerson", orgId, undefined, {
          id: person.id,
          accounts: [...person.accounts, { id: newCompany.id }],
        });
      }
    }
    const intl = i18nService.getIntl();
    const message = getCreationGeocodeErrorMessage(newCompany.geoStatus, intl);
    if (message) {
      notification.warning({ message });
    }
    if (callback) {
      yield call(callback, newCompany);
    }
    yield put(notifyAboutChanges({ added: [newCompany], entityType: EntityType.COMPANY }));
    const notificationKey = "create-company-notification";
    // The created record from the API response, not the form: the server may assign the default type
    const messageArgs: SourceObjectMessageArgs = yield call(
      selectSourceObjectMessageArgs,
      EntityType.COMPANY,
      newCompany.sourceObject
    );
    notification.success({
      duration: NOTIFICATION_DURATION_WITH_ACTION,
      key: notificationKey,
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(successMessage, "Company added successfully", messageArgs),
        () => {
          showEntityChannel.put(
            showEntityView({ entityId: newCompany.id, entityType: EntityType.COMPANY })
          );
          notification.close(notificationKey);
        }
      ),
    });

    yield put(clearAllUploadedCompanyFiles());
    yield put(createCompany.success(newCompany));
  } catch (error) {
    yield put(createCompany.failure({}));
    yield put(handleError({ error }));
  }
}

export function* onUploadCompanyFiles({ payload }: ReturnType<typeof uploadCompanyFiles.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const ownFileGroupId: string = yield select(getFileGroupId);
    // Empty until this modal's own initialize ran, which is exactly the case when another surface
    // (the chat proposal editor) is borrowing the uploader — so a supplied id wins.
    const fileGroupId = payload.fileGroupId ?? ownFileGroupId;
    const responses: SettleResult<RawFile>[] = yield allSettled(
      payload.files.map((file) =>
        callApi("createFile", org.id, file, undefined, undefined, undefined, {
          headers: {
            "x-mmc-file-group-id": fileGroupId,
          },
        })
      )
    );
    const fileList: FileListItem[] = responses.map((response, index) => ({
      file: payload.files[index],
      uploading: false,
      ...(response.error
        ? { errored: true, errorMessage: String(response.result) }
        : { errored: false, uploadedFile: response.result }),
    }));
    payload.callback?.(fileList);
    yield put(uploadCompanyFiles.success(fileList));
  } catch (error) {
    payload.callback?.(payload.files.map((file) => ({ errored: true, file, uploading: false })));
    yield put(uploadCompanyFiles.failure());
    yield put(handleError({ error }));
  }
}

export function* onRemoveCompanyFile({ payload }: ReturnType<typeof removeCompanyFile.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    yield callApi("deleteFile", org.id, payload.id);
    yield put(removeCompanyFile.success(payload.id));
  } catch (error) {
    yield put(removeCompanyFile.failure());
    yield put(handleError({ error }));
  }
}

export function* createCompanyModalSaga() {
  yield takeLatest(initialize.request, onInitialize);
  yield takeLatest(createCompany.request, onCreateCompany);
  yield takeEvery(uploadCompanyFiles.request, onUploadCompanyFiles);
  yield takeEvery(removeCompanyFile.request, onRemoveCompanyFile);
}
